import { FC, useEffect, useState } from "react";
import "./KeyGeneratorHeaderForm.scss";
import { Product, Products } from "../../../models/models";
import { Formik, Field, Form } from "formik";
import ActionButtons from "../../../assets/components/ActionButtons/ActionButtons";
import TextInput from "../../../assets/components/Inputs/TextInput/TextInput";
import NumberInput from "../../../assets/components/Inputs/NumberInput/NumberInput";
import * as Yup from "yup";
import Checkbox from "../../../assets/components/Checkbox/Checkbox";
import { GenerateKeysList, MAX_KEYS_QUANTITY } from "../model";
import Select from "../../../assets/components/Select/Select";
import { ActionValue } from "../../../redux/notification-slice";
import { ReactComponent as NoActiveProductsIcon } from "./Img/no-active-products.svg";

// The per-user limit is not part of the schema on purpose: a quantity above it
// has to reach the submit handler so the page can explain how to lift it
// instead of silently disabling the buttons.
const validateSchema = () =>
    Yup.object().shape({
        reseller: Yup.string().required("Required"),
        keysQuantity: Yup.number()
            .positive("Only positive days")
            .min(1, "Too Short!")
            .max(
                MAX_KEYS_QUANTITY,
                `Too Long! Maximum ${MAX_KEYS_QUANTITY} quantity.`
            )
            .integer("Only integer numbers!")
            .required("Required"),
        allowedActivationCount: Yup.number()
            .positive("Only positive days")
            .min(1, "Too Short!")
            .max(5, "Too Long! Maximum 5 quantity.")
            .integer("Only integer numbers!")
            .required("Required"),
    });

interface Props {
    products: Products;
    user: {
        id: number;
        name: string;
        role?: string;
        permissions?: string[];
    };
    generateFileHandler: (
        values: GenerateKeysList
    ) => Promise<void | ActionValue>;
    generateTableHandler: (
        values: GenerateKeysList
    ) => Promise<void | ActionValue>;
}

const KeyGeneratorForm: FC<Props> = ({
    products,
    user,
    generateFileHandler,
    generateTableHandler,
}) => {
    const activeProducts = products.filter(
        (product) => product.isDisabled !== true
    );

    const [isShowTestKey, setShow] = useState<boolean>(false);
    const [defaultName, setDefaultName] = useState<string>(user.name);
    const [productId, setProductId] = useState<Product["id"]>(
        activeProducts[0]?.id || 0
    );

    const setShowTestKey = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShow(!isShowTestKey);
        return event;
    };

    useEffect(() => {
        if (!isShowTestKey) {
            setDefaultName(user.name);
        } else {
            setDefaultName("Test - " + user.name);
        }
    }, [setDefaultName, isShowTestKey, user.name, setShow]);

    useEffect(() => {
        if (
            activeProducts.length > 0 &&
            !activeProducts.find((p) => p.id === productId)
        ) {
            setProductId(activeProducts[0].id);
        }
    }, [activeProducts, productId]);

    const handleGenerateFile = async (
        values: GenerateKeysList,
        setSubmitting: (isSubmitting: boolean) => void
    ) => {
        setSubmitting(true);
        await generateFileHandler(values);
        setSubmitting(false);
    };

    const handleGenerateTable = async (
        values: GenerateKeysList,
        setSubmitting: (isSubmitting: boolean) => void
    ) => {
        setSubmitting(true);
        await generateTableHandler(values);
        setSubmitting(false);
    };

    if (activeProducts.length === 0) {
        return (
            <div className="no-active-products">
                <div className="no-active-products__icon">
                    <NoActiveProductsIcon />
                </div>
                <div className="no-active-products__message">
                    No active products available. Please activate at least one
                    product to generate keys.
                </div>
            </div>
        );
    }

    return (
        <Formik
            initialValues={{
                keysQuantity: 1,
                userId: user.id,
                reseller: defaultName,
                testerKey: false,
                allowedActivationCount: 5,
            }}
            enableReinitialize
            validationSchema={validateSchema()}
            onSubmit={(values, { setSubmitting }) => {
                setSubmitting(true);
            }}
        >
            {({ isSubmitting, isValid, values, setSubmitting }) => {
                return (
                    <Form className="form_key_checker">
                        <div className="form_key_checker__row">
                            <Select
                                currentId={productId}
                                options={activeProducts}
                                label="Product Name *"
                                onChange={setProductId}
                            />

                            <NumberInput
                                label="Quantity:"
                                name="keysQuantity"
                                min={0}
                                max={MAX_KEYS_QUANTITY}
                            />
                        </div>
                        <div className="form_key_checker__row">
                            <Field
                                id="reseller"
                                label="Reseller *"
                                name="reseller"
                                as={TextInput}
                            />

                            <NumberInput
                                label="Allowed machine activations:"
                                name="allowedActivationCount"
                                min={0}
                                max={6}
                            />
                        </div>

                        <Checkbox
                            checked={isShowTestKey}
                            value={String(isShowTestKey)}
                            onChange={setShowTestKey}
                            name="testerKey"
                            label="Tester Key"
                        />

                        <div className="form_key_checker__footer">
                            <ActionButtons
                                className="form_key_checker__button_submit"
                                actionButtons={[
                                    {
                                        disabled: isSubmitting || !isValid,
                                        name: "generate to file",
                                        role: "secondary",
                                        onClick() {
                                            handleGenerateFile(
                                                {
                                                    keysQuantity:
                                                        values.keysQuantity,
                                                    productId,
                                                    userId: user.id,
                                                    reseller: values.reseller,
                                                    allowedActivationCount:
                                                        values.allowedActivationCount,
                                                    isGenerateFile: true,
                                                },
                                                setSubmitting
                                            );
                                        },
                                    },
                                    {
                                        disabled: isSubmitting || !isValid,
                                        name: "generate",
                                        role: "primary",
                                        onClick() {
                                            handleGenerateTable(
                                                {
                                                    keysQuantity:
                                                        values.keysQuantity,
                                                    productId,
                                                    userId: user.id,
                                                    reseller: values.reseller,
                                                    allowedActivationCount:
                                                        values.allowedActivationCount,
                                                    isGenerateFile: false,
                                                },
                                                setSubmitting
                                            );
                                        },
                                    },
                                ]}
                            />
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default KeyGeneratorForm;
